// A cart line is keyed by (id, sku) — two variants of the same product are two separate lines.
const CART_KEY = 'vbp_cart';

function getCart() {
	try {
		const raw = localStorage.getItem(CART_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
}

function saveCart(cart) {
	localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

function findLine(cart, id, sku) {
	return cart.find((item) => item.id === id && item.sku === sku);
}

// Cart lines saved before this change has no `stock` field — treat that as uncapped rather than 0.
function lineStockCap(item) {
	return Number.isFinite(item.stock) ? item.stock : Infinity;
}

function addToCart({ id, sku, name, price, attributes, qty, image, stock }) {
	const cart = getCart();
	const quantity = Math.max(1, parseInt(qty, 10) || 1);
	const existing = findLine(cart, id, sku);

	if (existing) {
		existing.qty = Math.min(lineStockCap(existing), existing.qty + quantity);
	} else {
		// Stock snapshot taken at add-to-cart time (design.md) — the server re-checks live stock at checkout regardless.
		const stockCap = Math.max(0, parseInt(stock, 10) || 0);
		cart.push({
			id,
			sku,
			name,
			price: parseInt(price, 10) || 0,
			attributes: attributes || {},
			image: image || '',
			stock: stockCap,
			qty: Math.min(stockCap, quantity),
		});
	}

	saveCart(cart);
	updateHeaderBadge();
	return cart;
}

function setQty(id, sku, qty) {
	let cart = getCart();
	const quantity = parseInt(qty, 10) || 0;

	if (quantity < 1) {
		cart = cart.filter((item) => !(item.id === id && item.sku === sku));
	} else {
		const existing = findLine(cart, id, sku);
		if (existing) {
			existing.qty = Math.min(lineStockCap(existing), quantity);
		}
	}

	saveCart(cart);
	updateHeaderBadge();
	return cart;
}

function removeFromCart(id, sku) {
	const cart = getCart().filter((item) => !(item.id === id && item.sku === sku));
	saveCart(cart);
	updateHeaderBadge();
	return cart;
}

function clearCart() {
	saveCart([]);
	updateHeaderBadge();
}

function getCartCount() {
	return getCart().reduce((sum, item) => sum + item.qty, 0);
}

function getCartTotal() {
	return getCart().reduce((sum, item) => sum + item.qty * item.price, 0);
}

// Separate key: subtotal-scoped, not a line item. Amount here is a preview only — server re-derives it before it counts (inc/order-data.php).
const DISCOUNT_KEY = 'vbp_cart_discount';

function getAppliedDiscount() {
	try {
		const raw = localStorage.getItem(DISCOUNT_KEY);
		const parsed = raw ? JSON.parse(raw) : null;
		return parsed && parsed.code ? parsed : null;
	} catch (e) {
		return null;
	}
}

function setAppliedDiscount(discount) {
	if (discount) {
		localStorage.setItem(DISCOUNT_KEY, JSON.stringify(discount));
	} else {
		localStorage.removeItem(DISCOUNT_KEY);
	}
}

function clearAppliedDiscount() {
	setAppliedDiscount(null);
}

function getDiscountAmount() {
	const discount = getAppliedDiscount();
	return discount ? discount.amount : 0;
}

function getCartFinalTotal() {
	return Math.max(0, getCartTotal() - getDiscountAmount());
}

// Preview only, checkout page only (design.md) — compared against the pre-discount subtotal; the server always re-derives this before an order is created.
function getShippingFee() {
	const { shippingFee, freeShippingThreshold } = window.vbpData || {};
	if (getCartTotal() >= (Number(freeShippingThreshold) || 0)) {
		return 0;
	}
	return Number(shippingFee) || 0;
}

function getCheckoutFinalTotal() {
	return Math.max(0, getCartTotal() - getDiscountAmount()) + getShippingFee();
}

function applyDiscountCode(code) {
	const items = getCart().map(({ id, sku, qty }) => ({ id, sku, qty }));
	const body = new URLSearchParams({
		action: 'vbp_check_discount_code',
		nonce: window.vbpData.checkoutNonce,
		code,
		items: JSON.stringify(items),
	});

	return fetch(window.vbpData.ajaxUrl, { method: 'POST', body })
		.then((res) => res.json())
		.then((data) => {
			if (!data.success) {
				clearAppliedDiscount();
				throw new Error(data.data?.message || 'Mã giảm giá không hợp lệ.');
			}
			setAppliedDiscount({ code: data.data.code, amount: data.data.amount });
			return data.data;
		});
}

function showDiscountMessage(message) {
	const messageEl = document.getElementById('discount-message');
	if (!messageEl) {
		return;
	}
	messageEl.textContent = message || '';
	messageEl.hidden = !message;
}

// Swaps input+button for a read-only chip once applied, so checkout doesn't re-show an editable field for a code already applied on the cart page.
function renderDiscountControls() {
	const formEl = document.getElementById('discount-form');
	const appliedEl = document.getElementById('discount-applied');
	const appliedCodeEl = document.getElementById('discount-applied-code');
	const discount = getAppliedDiscount();

	if (formEl) {
		formEl.hidden = !!discount;
	}
	if (appliedEl) {
		appliedEl.hidden = !discount;
	}
	if (appliedCodeEl && discount) {
		appliedCodeEl.textContent = `Đã áp dụng mã: ${discount.code}`;
	}
}

// Re-validates the applied code whenever the subtotal could change (qty/removal/reload) so the preview never goes stale.
function syncAppliedDiscount() {
	const discount = getAppliedDiscount();
	if (!discount) {
		showDiscountMessage('');
		renderCartPage();
		renderCheckoutSummary();
		return Promise.resolve(null);
	}

	return applyDiscountCode(discount.code)
		.then((result) => {
			showDiscountMessage('');
			return result;
		})
		.catch((err) => {
			showDiscountMessage(err.message);
			return null;
		})
		.finally(() => {
			renderCartPage();
			renderCheckoutSummary();
		});
}

function initDiscountControls() {
	const applyBtn = document.getElementById('discount-apply');
	const input = document.getElementById('discount-input');
	if (!applyBtn || !input) {
		return;
	}

	const applied = getAppliedDiscount();
	if (applied) {
		input.value = applied.code;
	}

	applyBtn.addEventListener('click', () => {
		const code = input.value.trim();
		if (!code) {
			return;
		}

		const originalLabel = applyBtn.textContent;
		applyBtn.textContent = 'Đang kiểm tra...';

		applyDiscountCode(code)
			.then(() => showDiscountMessage(''))
			.catch((err) => showDiscountMessage(err.message))
			.finally(() => {
				applyBtn.textContent = originalLabel;
				renderCartPage();
				renderCheckoutSummary();
			});
	});

	const removeBtn = document.getElementById('discount-remove');
	if (removeBtn) {
		removeBtn.addEventListener('click', () => {
			clearAppliedDiscount();
			input.value = '';
			showDiscountMessage('');
			renderCartPage();
			renderCheckoutSummary();
		});
	}
}

function formatVND(amount) {
	if (!amount || amount <= 0) {
		return 'Liên hệ';
	}
	return amount.toLocaleString('vi-VN') + 'đ';
}

function attributesLabel(attributes) {
	return Object.entries(attributes || {}).map(([axis, value]) => `${axis}: ${value}`).join(', ');
}

function updateHeaderBadge() {
	const count = getCartCount();
	document.querySelectorAll('[data-cart-count]').forEach((el) => {
		el.textContent = String(count);
	});
	document.querySelectorAll('[data-cart-label]').forEach((el) => {
		el.textContent = count + ' sản phẩm';
	});
}

function cartLineKey(item) {
	return `${item.id}::${item.sku}`;
}

function cartItemMarkup(item) {
	const label = attributesLabel(item.attributes);
	return `
		<div class="c-cart__row" data-cart-item="${cartLineKey(item)}" data-id="${item.id}" data-sku="${item.sku}">
			<div class="c-cart__prod c-cart__col1">
				<div class="c-cart__thumb">${item.image ? `<img loading="lazy" class="c-cart__image-inner u-img" src="${item.image}" alt="">` : ''}</div>
				<div class="c-cart__info">
					<div class="c-cart__name">${item.name}</div>
					${label ? `<div class="c-cart__var">${label}</div>` : ''}
				</div>
			</div>
			<div class="c-cart__price c-cart__col2"><span class="c-cart__price-txt">Đơn giá: </span>${formatVND(item.price)}</div>
			<div class="c-cart__qty c-cart__col3">
				<div class="c-cart__qty-inner">
					<div class="c-cart__qb" data-qty-decrease>−</div>
					<div class="c-cart__qv">${item.qty}</div>
					<div class="c-cart__qb" data-qty-increase>+</div>
				</div>
			</div>
			<div class="c-cart__total-price c-cart__col4">
				<div class="c-cart__total">${formatVND(item.price * item.qty)}</div>
			</div>
			<div class="c-cart__del c-cart__col5" data-cart-remove>
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="c-cart__del-icon">
					<path stroke="none" d="M0 0h24v24H0z" fill="none" />
					<path d="M18 6l-12 12" />
					<path d="M6 6l12 12" />
				</svg>
			</div>
		</div>
	`;
}

function renderCartPage() {
	const itemsEl = document.getElementById('cart-items');
	if (!itemsEl) {
		return;
	}

	const headerEl = document.getElementById('cart-header');
	const emptyEl = document.getElementById('cart-empty');
	const contentEl = document.getElementById('cart-content');
	const totalEl = document.getElementById('cart-total');
	const countEl = document.getElementById('cart-summary-count');
	const cart = getCart();

	const isEmpty = cart.length === 0;

	if (emptyEl) emptyEl.hidden = !isEmpty;
	if (contentEl) contentEl.hidden = isEmpty;
	if (headerEl) headerEl.hidden = isEmpty;

	itemsEl.innerHTML = cart.map(cartItemMarkup).join('');
	if (totalEl) {
		totalEl.textContent = formatVND(getCartTotal());
	}

	const discountRowEl = document.getElementById('cart-discount-row');
	const discountValEl = document.getElementById('cart-discount-val');
	const discount = getAppliedDiscount();
	if (discountRowEl) {
		discountRowEl.hidden = !discount;
	}
	if (discountValEl && discount) {
		discountValEl.textContent = `-${formatVND(discount.amount)}`;
	}
	renderDiscountControls();

	const totalFinalEl = document.getElementById('cart-total-final');
	if (totalFinalEl) {
		totalFinalEl.textContent = formatVND(getCartFinalTotal());
	}
	if (countEl) {
		countEl.textContent = `Tạm tính (${getCartCount()} sản phẩm)`;
	}
}

function checkoutItemMarkup(item) {
	const label = attributesLabel(item.attributes);
	return `
		<div class="c-buy__order-prod-row" data-cart-item="${cartLineKey(item)}">
			<div class="c-buy__order-prod-thumb">${item.image ? `<img loading="lazy" class="c-buy__order-prod-image u-img" src="${item.image}" alt="">` : ''}</div>
			<div class="c-buy__order-prod-info">
				<div class="c-buy__order-prod-name">${item.name}</div>
				${label ? `<div class="c-buy__order-prod-variant">· ${label}</div>` : ''}
				<div class="c-buy__order-prod-unit is-red">♦ ${formatVND(item.price)} × ${item.qty}</div>
			</div>
			<div class="c-buy__order-prod-subtotal">${formatVND(item.price * item.qty)}</div>
		</div>
	`;
}

function renderCheckoutSummary() {
	const itemsEl = document.getElementById('checkout-items');
	if (!itemsEl) {
		return;
	}

	const emptyEl = document.getElementById('checkout-empty');
	const contentEl = document.getElementById('checkout-content');
	const totalEl = document.getElementById('checkout-total');
	const cart = getCart();

	if (emptyEl) emptyEl.hidden = cart.length !== 0;
	if (contentEl) contentEl.hidden = cart.length === 0;

	itemsEl.innerHTML = cart.map(checkoutItemMarkup).join('');

	const subtotalEl = document.getElementById('checkout-subtotal');
	if (subtotalEl) {
		subtotalEl.textContent = formatVND(getCartTotal());
	}

	const shippingValEl = document.getElementById('checkout-shipping-value');
	if (shippingValEl) {
		const shippingFee = getShippingFee();
		// formatVND() reads 0 as "no price set" (Liên hệ) — wrong here, 0 is a legitimate free-shipping outcome.
		shippingValEl.textContent = shippingFee > 0 ? formatVND(shippingFee) : 'Miễn phí';
	}

	const discountRowEl = document.getElementById('checkout-discount-row');
	const discountValEl = document.getElementById('checkout-discount-val');
	const discount = getAppliedDiscount();
	if (discountRowEl) {
		discountRowEl.hidden = !discount;
	}
	if (discountValEl && discount) {
		discountValEl.textContent = `-${formatVND(discount.amount)}`;
	}
	renderDiscountControls();

	if (totalEl) {
		totalEl.textContent = formatVND(getCheckoutFinalTotal());
	}
}

document.addEventListener('click', (e) => {
	const addControl = e.target.closest('[data-add-to-cart]');
	if (addControl) {
		let qty = 1;
		if (addControl.dataset.qtySelector) {
			const qtyInput = document.querySelector(addControl.dataset.qtySelector);
			if (qtyInput) {
				qty = qtyInput.textContent || qtyInput.value;
			}
		}

		addToCart({
			id: addControl.dataset.id,
			sku: addControl.dataset.sku,
			name: addControl.dataset.name,
			price: addControl.dataset.price,
			attributes: addControl.dataset.attributes ? JSON.parse(addControl.dataset.attributes) : {},
			image: addControl.dataset.image,
			stock: addControl.dataset.stock,
			qty,
		});

		if (addControl.dataset.redirect) {
			window.location.href = addControl.dataset.redirect;
			return;
		}

		const originalLabel = addControl.textContent;
		addControl.textContent = 'Đã thêm ✓';
		addControl.classList.add('is-added');
		window.setTimeout(() => {
			addControl.textContent = originalLabel;
			addControl.classList.remove('is-added');
		}, 1500);
		return;
	}

	const row = e.target.closest('[data-cart-item]');
	if (!row) {
		return;
	}
	const { id, sku } = row.dataset;

	if (e.target.closest('[data-qty-increase]')) {
		const item = findLine(getCart(), id, sku);
		if (item) setQty(id, sku, item.qty + 1);
		syncAppliedDiscount();
	} else if (e.target.closest('[data-qty-decrease]')) {
		const item = findLine(getCart(), id, sku);
		if (item) setQty(id, sku, item.qty - 1);
		syncAppliedDiscount();
	} else if (e.target.closest('[data-cart-remove]')) {
		removeFromCart(id, sku);
		syncAppliedDiscount();
	}
});

document.addEventListener('DOMContentLoaded', () => {
	updateHeaderBadge();
	initDiscountControls();
	syncAppliedDiscount();
});

export {
	getCart,
	addToCart,
	setQty,
	removeFromCart,
	clearCart,
	getCartCount,
	getCartTotal,
	formatVND,
	updateHeaderBadge,
	renderCartPage,
	renderCheckoutSummary,
	getAppliedDiscount,
	clearAppliedDiscount,
	getCartFinalTotal,
	getShippingFee,
	getCheckoutFinalTotal,
};
